import { useState, useEffect, useCallback } from 'react';

interface ServiceWorkerUpdate {
  isUpdateAvailable: boolean;
  isInstalling: boolean;
  isOffline: boolean;
  needRefresh: boolean;
  updateServiceWorker: () => Promise<void>;
  checkForUpdates: () => Promise<void>;
}

let updateServiceWorker: (() => Promise<void>) | undefined;

export const useServiceWorkerUpdates = (): ServiceWorkerUpdate => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [needRefresh, setNeedRefresh] = useState(false);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize service worker registration and update handling
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const initServiceWorker = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
          
          // Check for waiting service worker (update available)
          if (registration.waiting) {
            setIsUpdateAvailable(true);
          }

          // Listen for service worker updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version is available
                  setIsUpdateAvailable(true);
                } else if (newWorker.state === 'activated') {
                  setNeedRefresh(true);
                }
              });
            }
          });

          // Listen for controlling service worker change
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            setIsInstalling(false);
            setNeedRefresh(true);
          });

          // Set up message listener for skip waiting
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'SKIP_WAITING') {
              setNeedRefresh(true);
            }
          });

          // Store the update function
          updateServiceWorker = async () => {
            if (registration.waiting) {
              setIsInstalling(true);
              // Send skip waiting message to the waiting service worker
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          };

        } catch (error) {
          console.error('Service worker registration failed:', error);
        }
      };

      initServiceWorker();
    }
  }, []);

  // Check for updates periodically and on app focus
  const checkForUpdates = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/sw.js');
        if (registration) {
          await registration.update();
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }
  }, []);

  // Check for updates when app becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        checkForUpdates();
      }
    };

    const handleFocus = () => {
      if (navigator.onLine) {
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Initial check
    if (!document.hidden && navigator.onLine) {
      checkForUpdates();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForUpdates]);

  const handleUpdateServiceWorker = useCallback(async () => {
    if (updateServiceWorker) {
      await updateServiceWorker();
    }
  }, []);

  return {
    isUpdateAvailable,
    isInstalling,
    isOffline,
    needRefresh,
    updateServiceWorker: handleUpdateServiceWorker,
    checkForUpdates,
  };
};